import { AlertTriangle, ArrowLeft, Briefcase, Clock, Loader2, MapPin } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { jobPostingApi } from '@/services/jobPostingService';
import { useApplicationStore } from '@/store/applicationStore';
import { useAuthStore } from '@/store/authStore';

import type { ActiveJobPosting } from '@/types/jobPosting.types';

const ROLE_BADGE_COLORS: Record<ActiveJobPosting['role'], string> = {
  armed: 'bg-red-100 text-red-700',
  unarmed: 'bg-blue-100 text-blue-700',
  supervisor: 'bg-purple-100 text-purple-700',
  patrol: 'bg-green-100 text-green-700',
  event: 'bg-amber-100 text-amber-700',
};

export function JobDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const initApplication = useApplicationStore((state) => state.initApplication);

  const [posting, setPosting] = useState<ActiveJobPosting | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeAppCount, setActiveAppCount] = useState(0);
  const [applicantState, setApplicantState] = useState<string | null>(null);
  const isLimitReached = activeAppCount >= 5;
  const isOutOfState = isAuthenticated && !!applicantState && !!posting && posting.siteState !== applicantState;

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    jobPostingApi
      .getActiveById(id)
      .then((result) => {
        if (result) {
          setPosting(result);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        setNotFound(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    useApplicationStore
      .getState()
      .checkExistingApplications()
      .then((apps) => {
        const activeApps = apps.filter((a) => a.status !== 'rejected');
        setActiveAppCount(activeApps.length);
        // Derive applicant state from existing applications' job posting locations
        const appWithLocation = activeApps.find((a) => a.jobPostingLocation);
        if (appWithLocation?.jobPostingLocation) {
          // Location format is "City, STATE"
          const parts = appWithLocation.jobPostingLocation.split(', ');
          const state = parts[parts.length - 1];
          if (state) setApplicantState(state);
        }
      })
      .catch(() => {
        /* ignore */
      });
  }, [isAuthenticated]);

  const handleApply = useCallback(() => {
    if (!posting || isLimitReached || isOutOfState) return;

    const navState = {
      jobPostingId: posting.jobPostingId,
      jobPostingTitle: posting.title,
      jobPostingLocation: `${posting.siteCity}, ${posting.siteState}`,
      jobPostingSiteState: posting.siteState,
    };

    if (isAuthenticated) {
      initApplication();
      void navigate('/wizard', { state: navState });
    } else {
      void navigate('/auth', { state: navState });
    }
  }, [posting, isAuthenticated, navigate, initApplication, isLimitReached, isOutOfState]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[60vh]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  if (notFound || !posting) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[60vh] px-4 text-center'>
        <Briefcase className='h-16 w-16 text-gray-300 mb-4' />
        <h2 className='text-xl font-semibold text-gray-900 mb-2'>{t('jobPostings.detail.notFound')}</h2>
        <p className='text-gray-500 mb-6'>{t('jobPostings.detail.notFoundSub')}</p>
        <Link
          to='/'
          className='px-8 py-3 bg-primary text-white text-lg rounded-lg font-bold hover:bg-primary-dark transition shadow-md'>
          {t('jobPostings.detail.backToListings')}
        </Link>
      </div>
    );
  }

  const requirementsList: string[] = Array.isArray(posting.requirements) ? (posting.requirements as string[]) : [];

  return (
    <div className='max-w-3xl mx-auto px-4 py-5 md:py-8'>
      {/* Back link */}
      <Link
        to='/'
        className='inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary transition mb-6'>
        <ArrowLeft className='h-4 w-4' />
        {t('jobPostings.detail.backToListings')}
      </Link>

      {/* Main card */}
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
        {/* Header section */}
        <div className='p-6 md:p-8 border-b border-gray-100'>
          <div className='flex items-start justify-between gap-4 mb-4'>
            <h1 className='text-3xl md:text-4xl font-bold text-gray-900'>{posting.title}</h1>
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-semibold',
                ROLE_BADGE_COLORS[posting.role],
              )}>
              {t(`jobPostings.roles.${posting.role}`)}
            </span>
          </div>

          <div className='flex items-center gap-2 text-gray-500'>
            <MapPin className='h-4 w-4' />
            <span className='text-sm'>
              {t('jobPostings.detail.address', {
                address: posting.siteAddress,
                city: posting.siteCity,
                state: posting.siteState,
                zip: posting.siteZip,
              })}
            </span>
          </div>
          {posting.closingDate && (
            <div className='flex items-center gap-2 text-amber-600 mt-1'>
              <Clock className='h-4 w-4' />
              <span className='text-sm font-medium'>
                {t('jobPostings.detail.closingDate', {
                  date: new Date(posting.closingDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }),
                })}
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className='p-6 md:p-8 space-y-6'>
          {/* Description */}
          {posting.description && (
            <div>
              <h2 className='text-xl font-semibold text-gray-900 mb-3'>{t('jobPostings.detail.description')}</h2>
              <p className='text-gray-600 leading-relaxed whitespace-pre-line'>{posting.description}</p>
            </div>
          )}

          {/* Requirements */}
          {requirementsList.length > 0 && (
            <div>
              <h2 className='text-xl font-semibold text-gray-900 mb-3'>{t('jobPostings.detail.requirements')}</h2>
              <ul className='space-y-2.5'>
                {requirementsList.map((req, index) => (
                  <li key={index} className='flex items-start gap-3 text-gray-600'>
                    <span className='mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0' />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Apply button */}
          {isLimitReached && isAuthenticated ? (
            <div className='rounded-lg border border-amber-200 bg-amber-50 p-4 text-center'>
              <div className='flex items-center justify-center gap-2 text-amber-700 mb-2'>
                <AlertTriangle className='h-5 w-5' />
                <span className='font-semibold'>{t('jobPostings.applicationLimit.reached')}</span>
              </div>
              <p className='text-sm text-amber-600'>{t('jobPostings.applicationLimit.reachedHint')}</p>
              <Link
                to='/dashboard'
                className='mt-3 inline-block text-sm font-medium text-primary hover:text-primary-dark'>
                {t('jobPostings.applicationLimit.bannerLink')}
              </Link>
            </div>
          ) : isOutOfState ? (
            <div className='rounded-lg border border-amber-200 bg-amber-50 p-4 text-center'>
              <div className='flex items-center justify-center gap-2 text-amber-700 mb-2'>
                <AlertTriangle className='h-5 w-5' />
                <span className='font-semibold'>
                  {t('jobPostings.stateRestriction.disabled', {
                    state: posting.siteState,
                    applicantState,
                  })}
                </span>
              </div>
            </div>
          ) : (
            <button
              type='button'
              onClick={handleApply}
              className='w-full py-4 bg-primary text-white text-xl font-bold rounded-lg hover:bg-primary-dark transition shadow-md shadow-primary/20'>
              {t('jobPostings.detail.applyNow')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
